<template>
    <div :class="{ 'book-detail-action-grid': $slots['companion-action'] }">
        <div v-if="variant === 'review-detail'" class="review-detail-library-control">
            <template v-if="currentStatus">
                <p class="mb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-foxy-text-secondary">У вашій бібліотеці</p>
                <div class="flex flex-wrap items-center gap-2" data-review-detail-library-actions>
                    <button
                        type="button"
                        class="inline-flex min-h-10 items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-bold transition-all hover:brightness-105 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/25 active:translate-y-px"
                        :class="detailStatusClasses"
                        aria-haspopup="dialog"
                        :aria-expanded="showQuickShelfPicker ? 'true' : 'false'"
                        @click="openReadingStatusModal"
                    >
                        <i v-if="detailStatusIcon" :class="detailStatusIcon" aria-hidden="true"></i>
                        <span>{{ detailStatusPresentation.label }}</span>
                        <i class="fas fa-chevron-down ml-0.5 text-[10px] opacity-65" aria-hidden="true"></i>
                    </button>
                    <button
                        type="button"
                        class="foxy-btn-secondary min-h-10 !rounded-lg !px-3 !py-2 !text-sm"
                        @click="$emit('manage', $event.currentTarget)"
                    >Керувати</button>
                </div>
            </template>
            <button
                v-else
                type="button"
                class="inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-xl border border-foxy-action/35 bg-foxy-action/10 px-4 py-2 text-sm font-bold text-foxy-action transition-colors hover:bg-foxy-action/15 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20 active:translate-y-px"
                aria-haspopup="dialog"
                :aria-expanded="showQuickShelfPicker ? 'true' : 'false'"
                @click="openReadingStatusModal"
            >
                <i class="fas fa-plus" aria-hidden="true"></i>
                Додати до бібліотеки
            </button>
        </div>

        <!-- Кнопка добавления в список чтения -->
        <div v-else-if="currentStatus" class="w-full">
            <div class="flex items-center gap-2 rounded-2xl border border-foxy-border bg-foxy-elevated">
                <button
                    @click="openReadingStatusModal"
                    class="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-semibold uppercase tracking-wide shadow-lg transition-all duration-300 hover:scale-[1.01] hover:shadow-xl"
                    :class="statusColors[currentStatus] || statusColors.default"
                >
                    <span class="text-white">{{ statusTexts[currentStatus] }}</span>
                </button>
                <button
                    @click="removeStatus"
                    class="flex items-center justify-center rounded-2xl bg-foxy-surface px-4 py-3 text-foxy-text-secondary transition-all duration-200 hover:bg-foxy-surface/80 hover:text-foxy-text"
                    title="Видалити статус"
                >
                    <i class="fa-solid fa-xmark"></i>
                </button>
            </div>
        </div>
        <button @click="openReadingStatusModal" v-else-if="variant !== 'review-detail'" class="foxy-btn-primary w-full !rounded-xl !px-8 !py-3 !text-base shadow-lg hover:shadow-xl">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Додати
        </button>

        <slot name="companion-action" />

        <!-- Кнопка добавления в добірку (только для авторизованных) -->
        <button
            @click="openCustomLibraryModal"
            v-if="isAuthenticated && variant !== 'review-detail'"
            :class="{ 'book-detail-collection-action': $slots['companion-action'] }"
            class="foxy-btn-secondary mt-3 w-full !rounded-xl !border-0 !px-8 !py-3 !text-base !font-bold shadow-lg hover:shadow-xl"
        >
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
            </svg>
            Додати до добірки
        </button>

        <!-- Модальное окно статусов чтения -->
        <add-to-library-modal
            v-if="isAuthenticated && variant !== 'review-detail'"
            :show="showModal"
            :book="book"
            :user-libraries="userLibraries"
            :current-status="currentStatus"
            @close="closeModal"
            @status-selected="handleStatusSelected"
            @remove-status="removeStatus"
            @open-custom-library="handleOpenCustomLibrary"
        />

        <quick-shelf-picker
            v-if="variant === 'review-detail'"
            :show="showQuickShelfPicker"
            :current-status="currentStatus"
            :anchor-element="quickShelfAnchor"
            :busy="statusMutationPending"
            @select="handleQuickShelfSelection"
            @close="closeQuickShelfPicker"
        />

        <!-- Модальное окно выбора добірки -->
        <div v-if="showCustomLibraryModal" class="foxy-modal-overlay z-50 flex items-center justify-center p-4">
            <div
                class="foxy-modal mx-4 w-full max-w-md transform !border-0 transition-all duration-300"
                :class="{ 'scale-100 opacity-100': showCustomLibraryModal, 'scale-95 opacity-0': !showCustomLibraryModal }"
            >
                <div class="p-6">
                    <div class="mb-6 flex items-center justify-between">
                        <h3 class="text-2xl font-bold text-foxy-text">Додати до добірки</h3>
                        <button @click="closeCustomLibraryModal" class="text-foxy-text-secondary transition-colors hover:text-foxy-text">
                            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <!-- Список существующих добірок, где уже есть книга -->
                    <div v-if="currentBookLibraries.length > 0" class="mb-6">
                        <label class="mb-3 block text-sm font-medium text-foxy-text-secondary"> Книга вже в добірках: </label>
                        <div class="space-y-2">
                            <div
                                v-for="library in currentBookLibraries"
                                :key="library.id"
                                class="flex items-center justify-between rounded-xl bg-foxy-elevated p-3"
                            >
                                <div class="flex items-center space-x-2">
                                    <i class="fas fa-folder text-foxy-action"></i>
                                    <span class="font-medium text-foxy-text">{{ library.name }}</span>
                                    <span v-if="library.is_private" class="text-xs text-foxy-text-secondary">(Приватна)</span>
                                    <span v-else class="text-xs text-foxy-text-secondary">(Публічна)</span>
                                </div>
                                <button
                                    @click="removeFromLibrary(library)"
                                    class="p-1 text-foxy-error transition-colors hover:text-foxy-error/80"
                                    title="Видалити з добірки"
                                >
                                    <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    <form @submit.prevent="submitToCustomLibrary">
                        <div class="mb-6">
                            <label class="mb-3 block text-sm font-medium text-foxy-text-secondary">Оберіть добірку</label>
                            <select v-model="selectedLibraryId" required class="foxy-select">
                                <option value="">-- Оберіть добірку --</option>
                                <option v-for="library in availableLibraries" :key="library.id" :value="library.id">
                                    {{ library.name }}
                                    <span v-if="library.is_private">(Приватна)</span>
                                    <span v-else>(Публічна)</span>
                                </option>
                            </select>
                        </div>

                        <div class="flex space-x-3">
                            <button type="submit" class="foxy-btn-primary flex-1 !rounded-xl !px-6 !py-3 !text-base !font-bold">Додати</button>
                            <button
                                type="button"
                                @click="closeCustomLibraryModal"
                                class="foxy-btn-secondary flex-1 !rounded-xl !border-0 !px-6 !py-3 !text-base !font-bold"
                            >
                                Скасувати
                            </button>
                        </div>
                    </form>

                    <div v-if="userLibraries.length === 0" class="mt-4 rounded-xl bg-foxy-elevated p-4">
                        <p class="mb-3 text-sm text-foxy-text-secondary">У вас ще немає добірок</p>
                        <a :href="profileCollectionsUrl" class="text-sm font-medium text-foxy-action hover:text-foxy-action/80">
                            Створити першу добірку →
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { shelfChoiceRequest } from '../utils/quickPreviewActions';
import { bookStatusPresentation } from '../utils/bookStatusPresentation';
import { requestLibraryRemoval } from '../utils/libraryRemoval';

export default {
    name: 'AddToLibraryButton',
    props: {
        book: {
            type: Object,
            required: true,
        },
        userLibraries: {
            type: Array,
            default: () => [],
        },
        isAuthenticated: {
            type: Boolean,
            default: false,
        },
        initialStatus: {
            type: String,
            default: null,
        },
        statusFromServer: {
            type: Boolean,
            default: false,
        },
        variant: {
            type: String,
            default: 'default'
        }
    },
    data() {
        // Ініціалізуємо статус синхронно з кешу для миттєвого відображення
        let initialStatus = this.initialStatus;
        // Do not revive deleted statuses from localStorage when server already passed the truth
        if (!this.statusFromServer && !initialStatus && this.isAuthenticated && this.book && this.book.id && window.bookStatusCache) {
            const cachedStatus = window.bookStatusCache.get(this.book.id);
            if (cachedStatus && cachedStatus.status) {
                initialStatus = cachedStatus.status;
            }
        }

        return {
            showModal: false,
            showQuickShelfPicker: false,
            quickShelfAnchor: null,
            statusMutationPending: false,
            showCustomLibraryModal: false,
            selectedLibraryId: '',
            currentStatus: initialStatus,
            readingStatusLoaded: false,
            readingStatusLockVersion: null,
            openReadingInstanceId: null,
            openReadingState: null,
            readingContextInstanceId: null,
            readingContextState: null,
            currentBookLibraries: [],
            statusTexts: {
                read: 'Прочитано',
                reading: 'Читаю',
                want_to_read: 'Буду читати',
                abandoned: 'Не дочитано',
            },
            statusColors: {
                default: 'bg-foxy-action hover:bg-foxy-action/90',
                read: 'bg-foxy-action hover:bg-foxy-action/90',
                reading: 'bg-foxy-action hover:bg-foxy-action/90',
                want_to_read: 'bg-foxy-action hover:bg-foxy-action/90',
                abandoned: 'bg-foxy-action hover:bg-foxy-action/90',
            },
        };
    },
    created() {
        // Prefer server-provided status on book pages; keep cache in sync
        if (this.statusFromServer && this.isAuthenticated && this.book && this.book.id && window.bookStatusCache) {
            if (this.initialStatus) {
                window.bookStatusCache.set(this.book.id, { status: this.initialStatus });
            } else {
                window.bookStatusCache.remove(this.book.id);
            }
            return;
        }

        // Instant paint from cache for catalog cards
        if (this.isAuthenticated && this.book && this.book.id && window.bookStatusCache) {
            const cachedStatus = window.bookStatusCache.get(this.book.id);
            if (cachedStatus && cachedStatus.status) {
                this.currentStatus = cachedStatus.status;
            }
        }
    },
    mounted() {
        if (this.isAuthenticated) {
            // Always revalidate with server so removals from cabinet stay in sync
            this.loadStatusFromServer();
            if (this.variant !== 'review-detail') this.loadBookLibraries();
        }
    },
    computed: {
        detailStatusPresentation() {
            return bookStatusPresentation(this.currentStatus);
        },
        detailStatusIcon() {
            return {
                reading: 'fas fa-book-open',
                read: 'fas fa-check',
                abandoned: 'fas fa-minus',
            }[this.currentStatus] || '';
        },
        detailStatusClasses() {
            return {
                want_to_read: 'border-[#fff9f2] bg-[#5b458c] text-[#fffaf7] dark:border-[#f7efe9] dark:bg-[#7663b2] dark:text-[#fffaf7]',
                reading: 'border-[#fff9f2] bg-[#2e6095] text-[#fffaf7] dark:border-[#f7efe9] dark:bg-[#3d75ae] dark:text-[#fffaf7]',
                read: 'border-[#fff9f2] bg-[#356b4a] text-[#fffaf7] dark:border-[#f7efe9] dark:bg-[#477b59] dark:text-[#fffaf7]',
                abandoned: 'border-[#fff9f2] bg-[#9a4b3d] text-[#fffaf7] dark:border-[#f7efe9] dark:bg-[#a95542] dark:text-[#fffaf7]',
            }[this.currentStatus] || 'border-foxy-border bg-foxy-elevated text-foxy-text';
        },
        profileCollectionsUrl() {
            if (this.$root.$data.user && this.$root.$data.user.username) {
                return `/users/${this.$root.$data.user.username}/collections`;
            }
            return '#';
        },
        availableLibraries() {
            // Доборки, в которых книга еще не добавлена
            const bookLibraryIds = this.currentBookLibraries.map((lib) => lib.id);
            return this.userLibraries.filter((lib) => !bookLibraryIds.includes(lib.id));
        },
    },
    methods: {
        applyCanonicalReadingSnapshot(snapshot) {
            if (!snapshot || typeof snapshot !== 'object' || !Object.prototype.hasOwnProperty.call(snapshot, 'status')) {
                throw new Error('Canonical reading status is missing from the server response.');
            }

            this.currentStatus = snapshot.status ?? null;
            this.readingStatusLockVersion = snapshot.lock_version ?? null;
            this.openReadingInstanceId = snapshot.open_reading_instance_id ?? null;
            this.openReadingState = snapshot.open_reading_state ?? null;
            this.readingContextInstanceId = snapshot.reading_context_instance_id ?? null;
            this.readingContextState = snapshot.reading_context_state ?? null;
            this.readingStatusLoaded = true;

            const bookId = this.book?.id;
            if (bookId && window.bookStatusCache) {
                if (this.currentStatus) {
                    window.bookStatusCache.set(bookId, {
                        status: this.currentStatus,
                        times_read: snapshot.times_read ?? 0,
                        reading_language: snapshot.reading_language ?? null,
                        rating: snapshot.rating ?? null,
                        review: snapshot.review ?? null,
                        started_at: snapshot.started_at ?? null,
                        finished_at: snapshot.finished_at ?? null,
                    });
                } else {
                    window.bookStatusCache.remove(bookId);
                }
            }

            this.$emit('status-updated', this.currentStatus);
            if (this.variant === 'review-detail') this.$emit('status-snapshot', snapshot);
        },
        openReadingStatusModal(event = null) {
            // Проверяем авторизацию перед открытием модального окна
            if (!this.isAuthenticated) {
                // Сохраняем текущий URL для редиректа после логина
                const currentUrl = window.location.href;
                window.location.href = `/login?redirect=${encodeURIComponent(currentUrl)}`;
                return;
            }
            if (this.variant === 'review-detail') {
                this.quickShelfAnchor = event?.currentTarget || null;
                this.showQuickShelfPicker = true;
                return;
            }
            this.showModal = true;
        },
        closeModal() {
            this.showModal = false;
        },
        closeQuickShelfPicker({ restoreFocus = false } = {}) {
            const anchor = this.quickShelfAnchor;
            this.showQuickShelfPicker = false;
            this.quickShelfAnchor = null;
            if (restoreFocus) this.$nextTick(() => anchor?.focus());
        },
        async handleQuickShelfSelection(status) {
            this.closeQuickShelfPicker();
            await this.handleStatusSelected(status);
        },
        async handleStatusSelected(status) {
            if (this.statusMutationPending) return;
            this.statusMutationPending = true;
            try {
                // Використовуємо ID з об'єкта книги, якщо він є
                const bookId = this.book.id || (await this.getBookIdBySlug(this.book.slug));

                // Отправляем запрос на сервер
                const normalizedStatus = status === 'want-to-read' ? 'want_to_read' : status;
                const request = shelfChoiceRequest(normalizedStatus, { book: { id: bookId } });
                const response = await axios[request.method](request.url, request.data);

                if (response.data.success) {
                    this.applyCanonicalReadingSnapshot({
                        ...response.data.data,
                        open_reading_instance_id: response.data.open_reading_instance_id ?? null,
                        open_reading_state: response.data.open_reading_state ?? null,
                    });

                    this.showAlert('Книга додана до списку!', 'Успіх', 'success');
                } else {
                    this.showAlert(response.data.message || 'Помилка при збереженні статусу', 'Помилка', 'error');
                }
            } catch (error) {
                console.error('Error:', error);
                if (error?.response?.status === 409) {
                    this.showAlert(
                        error.response.data?.message || 'Статус читання змінився. Дані книги оновлено.',
                        'Потрібне оновлення',
                        'warning',
                    );
                    await this.loadStatusFromServer();
                } else {
                    this.showAlert('Помилка при збереженні статусу', 'Помилка', 'error');
                }
            } finally {
                this.statusMutationPending = false;
            }

            this.closeModal();
        },
        async removeStatus() {
            try {
                // Використовуємо ID з об'єкта книги, якщо він є
                const bookId = this.book.id || (await this.getBookIdBySlug(this.book.slug));
                if (!this.readingStatusLoaded) {
                    await this.loadStatusFromServer();
                }
                const result = await requestLibraryRemoval((payload) => axios.delete(`/api/reading-status/book/${bookId}`, { data: payload }), {
                    lockVersion: this.readingStatusLockVersion,
                    openReadingInstanceId: this.openReadingInstanceId,
                    openReadingState: this.openReadingState,
                });
                if (result.stale) {
                    this.showAlert('Статус читання змінився. Дані книги оновлено.', 'Потрібне оновлення', 'warning');
                    await this.loadStatusFromServer();
                    return;
                }
                if (!result.completed) return;

                const response = result.response;

                if (response.data.success) {
                    this.currentStatus = result.status;
                    this.readingStatusLockVersion = response.data.lock_version ?? null;
                    this.openReadingInstanceId = null;
                    this.openReadingState = null;
                    this.readingStatusLoaded = true;

                    if (window.bookStatusCache) {
                        if (result.status) {
                            window.bookStatusCache.set(bookId, { status: result.status });
                        } else {
                            window.bookStatusCache.remove(bookId);
                        }
                    }

                    this.showAlert(response.data.message, 'Успіх', 'success');
                    this.$emit('status-updated', result.status);
                } else {
                    this.showAlert(response.data.message || 'Помилка при видаленні статусу', 'Помилка', 'error');
                }
            } catch (error) {
                console.error('Error:', error);
                this.showAlert('Помилка при видаленні статусу', 'Помилка', 'error');
            }
        },
        async loadStatusFromServer() {
            if (!this.isAuthenticated || !this.book) return;

            // Використовуємо ID з об'єкта книги, якщо він є
            let bookId = this.book.id;

            // Якщо ID немає, отримуємо його
            if (!bookId && this.book.slug) {
                bookId = await this.getBookIdBySlug(this.book.slug);
                if (!bookId) return;
            }

            try {
                const response = await axios.get(`/api/reading-status/book/${bookId}`, {
                    timeout: 5000,
                });

                this.applyCanonicalReadingSnapshot({
                    ...response.data,
                    status: response.data?.status ?? null,
                });
            } catch (error) {
                // Ігноруємо помилки переривання запиту
                if (error.code === 'ECONNABORTED' || error.message === 'Request aborted') {
                    return;
                }

                // 404 = status removed
                if (error.response && error.response.status === 404) {
                    if (window.bookStatusCache) {
                        window.bookStatusCache.remove(bookId);
                    }
                    this.currentStatus = null;
                    this.readingStatusLockVersion = null;
                    this.openReadingInstanceId = null;
                    this.openReadingState = null;
                    this.readingStatusLoaded = true;
                    return;
                }

                // Інші помилки логуємо тільки якщо це не 404
                if (error.response && error.response.status !== 404) {
                    console.error('Error loading reading status:', error);
                }
            }
        },
        async getBookIdBySlug(slug) {
            if (!slug) return null;

            // Спочатку перевіряємо кеш
            if (window.bookIdCache) {
                const cachedId = window.bookIdCache.get(slug);
                if (cachedId) {
                    return cachedId;
                }
            }

            try {
                const response = await axios.get(`/api/books/${slug}/id`, {
                    timeout: 5000, // Таймаут 5 секунд
                });

                if (response.data && response.data.id) {
                    // Зберігаємо в кеш
                    if (window.bookIdCache) {
                        window.bookIdCache.set(slug, response.data.id);
                    }
                    return response.data.id;
                }

                return null;
            } catch (error) {
                // Ігноруємо помилки переривання запиту (коли компонент розмонтовується)
                if (error.code === 'ECONNABORTED' || error.message === 'Request aborted') {
                    return null;
                }

                // Інші помилки логуємо тільки якщо це не 404
                if (error.response && error.response.status !== 404) {
                    console.error('Error getting book ID:', error);
                }
                return null;
            }
        },
        handleOpenCustomLibrary() {
            this.closeModal();
            this.openCustomLibraryModal();
        },
        async openCustomLibraryModal() {
            // Проверяем авторизацию перед открытием модального окна
            if (!this.isAuthenticated) {
                // Сохраняем текущий URL для редиректа после логина
                const currentUrl = window.location.href;
                window.location.href = `/login?redirect=${encodeURIComponent(currentUrl)}`;
                return;
            }
            await this.loadBookLibraries(); // Загружаем актуальный список библиотек
            this.showCustomLibraryModal = true;
        },
        closeCustomLibraryModal() {
            this.showCustomLibraryModal = false;
            this.selectedLibraryId = '';
        },
        async submitToCustomLibrary() {
            if (!this.selectedLibraryId) {
                this.showAlert('Оберіть добірку', 'Помилка', 'error');
                return;
            }

            try {
                const url = `/libraries/${this.selectedLibraryId}/add-book`;
                const formData = new FormData();
                formData.append('book_slug', this.book.slug);

                const response = await axios.post(url, formData);

                if (response.data.success) {
                    await this.loadBookLibraries(); // Обновляем список библиотек
                    this.showAlert('Книга успішно додана до добірки!', 'Успіх', 'success');
                    this.selectedLibraryId = '';
                } else {
                    this.showAlert(response.data.message || 'Помилка при додаванні книги', 'Помилка', 'error');
                }
            } catch (error) {
                console.error('Error:', error);
                this.showAlert('Помилка при додаванні книги', 'Помилка', 'error');
            }
        },
        async loadBookLibraries() {
            if (!this.isAuthenticated) return;

            try {
                const response = await axios.get(`/api/books/${this.book.slug}/libraries`, {
                    timeout: 5000, // Таймаут 5 секунд
                });
                if (response.data && response.data.success) {
                    this.currentBookLibraries = response.data.libraries || [];
                }
            } catch (error) {
                // Ігноруємо помилки переривання запиту
                if (error.code === 'ECONNABORTED' || error.message === 'Request aborted') {
                    return;
                }

                // Игнорируем ошибку, если библиотеки не найдены
                if (error.response && error.response.status !== 404) {
                    console.error('Error loading book libraries:', error);
                }
            }
        },
        async removeFromLibrary(library) {
            try {
                const response = await axios.delete(`/libraries/${library.id}/books/${this.book.id}`);

                if (response.data.success) {
                    this.currentBookLibraries = this.currentBookLibraries.filter((lib) => lib.id !== library.id);
                    this.showAlert(`Книгу видалено з добірки "${library.name}"`, 'Успіх', 'success');
                } else {
                    this.showAlert(response.data.message || 'Помилка при видаленні книги', 'Помилка', 'error');
                }
            } catch (error) {
                console.error('Error:', error);
                this.showAlert(error.response?.data?.message || 'Помилка при видаленні книги', 'Помилка', 'error');
            }
        },
        showAlert(message, title, type = 'info') {
            if (window.alertModalInstance && window.alertModalInstance.$refs && window.alertModalInstance.$refs.modal) {
                window.alertModalInstance.$refs.modal.alert(message, title, type);
            } else {
                // Fallback если модалка не готова
                setTimeout(() => {
                    if (window.alertModalInstance && window.alertModalInstance.$refs && window.alertModalInstance.$refs.modal) {
                        window.alertModalInstance.$refs.modal.alert(message, title, type);
                    }
                }, 100);
            }
        },
    },
};
</script>

<style scoped>
.book-detail-action-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 48px;
    align-items: stretch;
    gap: 12px;
}

.book-detail-action-grid > :first-child {
    min-width: 0;
    min-height: 48px;
}

.book-detail-collection-action {
    grid-column: 1 / -1;
    margin-top: 0;
}
</style>
